import queryString from 'query-string';
import { get } from 'lodash';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_TAG_DETAIL_SET_DATA,
  CATALOG_TAG_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import { Tag } from 'rebook/apps/catalog/models';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import pluralize from 'rebook/apps/utils/text/pluralize';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, tagDetail: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const orderBy = get(ssr, 'query.o', 'popular');
    const subscriptionId = get(ssr, 'query.s', null);
    const bookType = get(ssr, 'query.t', null);

    const { tagSlug } = match.params;
    let newState = {
      tagSlug,
      pageNumber,
      orderBy,
      subscriptionId,
      bookType,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.tag) {
      newState = {
        ...newState,
        ...currentState,
      };
    }
    dispatch(setData(newState));

    if (!newState.tag) {
      await dispatch(fetchDetail(match));
    }

    if (!newState.books || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchBookList({}, pageNumber));
    }
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { tagSlug } = match.params;
    const url = `/api/tags/s-${tagSlug}/`;
    return await fetchQuery(url, { headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({ tag: data }));
      })
      .catch(({ data, resp, error }) => {
        if (resp.status === 404) {
          dispatch(Error404Actions.show());
        }
        else {
          Raven.captureMessage('failed to load tag detail due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
              error,
            },
          });
        }
      });
  };
}

export function fetchBookList(params = {}) {
  return async(dispatch, getState) => {
    const { tagDetail, ssr } = getState();
    const mergedState = { ...tagDetail, ...params };
    const { tag, paginateBy, orderBy, subscriptionId, bookType } = mergedState;
    let pageNumber = mergedState.pageNumber || 1;

    if (!params.pageNumber &&
      (tagDetail.subscriptionId !== mergedState.subscriptionId)
      ||(tagDetail.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }
    if (!tag) {
      return;
    }

    let queryParams = {
      tag: tag.id,
      offset: (pageNumber - 1) * paginateBy,
      limit: paginateBy,
      o: orderBy,
      author_weight__gte: 0,
      ...(subscriptionId!==null && { subscription_id__in: subscriptionId }),
      ...(bookType!==null && { type: bookType }),
    };
    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    const pendingState = {
      books: null,
      pageNumber,
      orderBy,
      lastPageNumber: 0,
      ...(subscriptionId!==null && { subscriptionId }),
      ...(bookType!==null && { bookType }),
    };

    dispatch(setData(pendingState));

    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        const totalBookCount = get(data, 'meta.total_count', 1);
        const lastPageNumber = Math.ceil(totalBookCount / paginateBy);
        dispatch(setData({
          books: data.objects,
          lastPageNumber: lastPageNumber,
          totalBookCount: totalBookCount,
          subscriptionId,
          bookType,
        }));
      })
      .catch(({ data, resp, error }) => {
        Raven.captureMessage('failed to load tag books due to server error', {
          level: 'error',
          extra: {
            status: resp ? resp.status : null,
            response: data,
            error,
          },
        });
      });
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { tagDetail } = getState();
    const tag = new Tag(tagDetail.tag);
    const isAudio = tagDetail.bookType === 'audio';
    const pageNumber = tagDetail.pageNumber;
    const title = isAudio
      ? `«${tag.name}» — слушать онлайн аудиокниги по теме на MyBook.`
      : `Книги по теме «${tag.name}» `+
        `(${tag.active_book_count} книг) — последние поступления и популярные книги `+
        `в электронной библиотеке MyBook`;
    const description = isAudio
      ? `Слушайте онлайн аудиокниги по теме «${tag.name}». Полный список популярных аудиокниг, `+
        `рейтинг и отзывы читателей в электронной библиотеке MyBook.`
      : `Читать онлайн ` +
        `${tag.active_book_count} ${pluralize(tag.active_book_count, 'книга', 'книги', 'книг')} ` +
        `по теме «${tag.name}». ` +
        `Полный список популярных книг по теме «${tag.name}», рейтинг и отзывы читателей  ` +
        `в электронной библиотеке MyBook. ${getPageNumberString(pageNumber)}`.trim();
    const canonicalUrl = `https://${BASE_HOST}${tag.getUrl()}`;
    dispatch(SeoActions.setData({
      title,
      description,
      canonicalUrl,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { tag } = payload;
    dispatch({
      type: CATALOG_TAG_DETAIL_SET_DATA,
      payload,
    });
    if (tag) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_TAG_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
